/**
 * Timeline Helper Functions
 * Utilities for formatting and displaying project timeline events
 * Extracted from ProjectDetailPage for reusability
 */


/**
 * Get display title for a history change type
 */
export function getHistoryTitle(changeType) {
  const titles = {
    'project_created': 'Project Created',
    'sid_assigned': 'SID Assigned',
    'sid_reassigned': 'SID Reassigned',
    'status_changed': 'Status Changed',
    'phase_changed': 'Phase Changed',
    'task_created': 'Task Created',
    'task_assigned': 'Task Assigned',
    'file_uploaded': 'File Uploaded',
    'package_price_updated': 'Price Updated',
    '2d_approved': '2D Design Approved',
    '3d_approved': '3D Design Approved',
    // Measurement events
    'measurement_scheduled': 'Measurement Scheduled',
    'measurement_assigned': 'Measurement Assigned',
    'measurement_started': 'Measurement Started',
    'measurement_completed': 'Measurement Completed',
    'measurement_cancelled': 'Measurement Cancelled',
    'measurement_rescheduled': 'Measurement Rescheduled',
    // Team management
    'team_member_added': 'Team Member Added',
    'team_member_removed': 'Team Member Removed'
  };
  return titles[changeType] || changeType;
}

/**
 * Get icon name for a history change type
 */
export function getHistoryIcon(changeType) {
  const icons = {
    'project_created': 'document',
    'sid_assigned': 'user',
    'sid_reassigned': 'user',
    'status_changed': 'status',
    'phase_changed': 'arrow',
    'task_created': 'task',
    'task_assigned': 'user',
    'file_uploaded': 'upload',
    'package_price_updated': 'money',
    '2d_approved': 'check',
    '3d_approved': 'check',
    // Measurement events
    'measurement_scheduled': 'calendar',
    'measurement_assigned': 'user',
    'measurement_started': 'play',
    'measurement_completed': 'check',
    'measurement_cancelled': 'x',
    'measurement_rescheduled': 'calendar',
    // Team management
    'team_member_added': 'user',
    'team_member_removed': 'user'
  };
  return icons[changeType] || 'dot';
}

/**
 * Get color for a history change type
 */
export function getHistoryColor(changeType) {
  const colors = {
    'project_created': 'blue',
    'sid_assigned': 'green',
    'sid_reassigned': 'yellow',
    'status_changed': 'purple',
    'phase_changed': 'indigo',
    'task_created': 'blue',
    'task_assigned': 'green',
    'file_uploaded': 'blue',
    'package_price_updated': 'orange',
    '2d_approved': 'green',
    '3d_approved': 'green',
    // Measurement events
    'measurement_scheduled': 'orange',
    'measurement_assigned': 'blue',
    'measurement_started': 'indigo',
    'measurement_completed': 'green',
    'measurement_cancelled': 'red',
    'measurement_rescheduled': 'yellow',
    // Team management
    'team_member_added': 'green',
    'team_member_removed': 'red'
  };
  return colors[changeType] || 'gray';
}

/**
 * Format history description, replacing UUIDs with readable names
 */
export function formatHistoryDescription(description, changeType, project) {
  if (!description) return '';

  // UUID pattern (8-4-4-4-12 hex characters)
  const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

  // For SID assignment/reassignment, replace UUID with SID name
  if (['sid_assigned', 'sid_reassigned'].includes(changeType)) {
    // Get SID from teamMembers array
    const sidMember = project?.teamMembers?.find(
      tm => (tm.roleType === 'sid' || tm.roleType === 'asid') && tm.isActive !== false
    );
    const projectSid = sidMember?.user;
    if (projectSid) {
      const sidName = projectSid.nickname || projectSid.fullName;
      // Replace the UUID with the SID name
      return description.replace(uuidPattern, sidName);
    }
  }

  // For other types, just return the description as is
  return description;
}

/**
 * Filter timeline history by type
 */
export function filterHistory(history, filterType) {
  if (filterType === 'all') return history;

  return history.filter(item => {
    if (filterType === 'task') return item.changeType.includes('task');
    if (filterType === 'sid') return item.changeType.includes('sid');
    if (filterType === 'measurement') return item.changeType.includes('measurement');
    return item.changeType === filterType;
  });
}

/**
 * Timeline filter options
 */
export const TIMELINE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'status_changed', label: 'Status' },
  { key: 'measurement', label: 'Measurement' },
  { key: 'file_uploaded', label: 'Files' },
  { key: 'task', label: 'Tasks' },
  { key: 'sid', label: 'SID' }
];
